import React, { useCallback, useState } from 'react';
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSyncedMediaResource } from '@brightmls/nextgen.lae.flc';
import { MediaCategory, MediaResource } from '../../../system/media';
import {
  ResourceField,
  useResourceForm,
} from '../hooks/useResourceForm/useResourceForm';
import { ListingResource } from '../../../system/field';
import { generateDataTestId, toHTTPSURL } from '../../../utils';
import MediaEditDeleteButtons from './MediaEditDeleteButtons';
import { useMediaQuery } from '@mui/system';

const MEDIA_VISIBILITY = {
  'Consumer Facing': 'Agents and Clients',
  AgentOnly: 'Agents Only',
};

const DocumentFields: ResourceField[] = [
  {
    Key: '$',
    Layout: 'single',
    Control: 'DocumentPreview',
    Metadata: {
      Key: '$',
      Type: 'Edm.String',
    },
  },
  {
    Key: 'MEDIA_SHORT_DESCRIPTION',
    Label: 'Name',
    Layout: 'single',
    ForceNeverDisabled: true,
    ForceRequired: true,
    Metadata: {
      Key: 'MEDIA_SHORT_DESCRIPTION',
      Type: 'Edm.String',
    },
  },
  {
    Key: 'MEDIA_LONG_DESCRIPTION',
    Label: 'Description',
    Layout: 'single',
    Control: 'TextArea',
    ForceNeverDisabled: true,
    Metadata: {
      Key: 'MEDIA_LONG_DESCRIPTION',
      Type: 'Edm.String',
    },
  },
];

const DocumentTile = (props: any) => {
  const {
    file,
    onDeleteDocument: deleteDocument,
    selectedFiles,
    onAddDocumentToSelected,
  } = props;
  const { openResourceForm } = useResourceForm();
  const { items: documents, updateMedia } = useSyncedMediaResource([
    MediaCategory.Document,
    MediaCategory.FloorPlan,
  ]);
  const [isConfirmCancel, setIsConfirmCancel] = useState(false);
  const [permissions, setPermissions] = useState(file.MEDIA_VISIBILITY);
  const theme = useTheme();
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('sm'));

  const saveHook = useCallback(
    (updatedFile: any) => {
      // Find the media item we're updating
      const itemIndex = documents.findIndex(
        (item: any) => item.MEDIA_ITEM_NUMBER === file.MEDIA_ITEM_NUMBER,
      );

      if (itemIndex === -1) return updatedFile;

      const updatedList = [...documents];
      updatedList[itemIndex] = updatedFile;
      updateMedia(updatedList);

      return updatedFile;
    },
    [file, documents, updateMedia],
  );

  const showEditSidePanel = () => {
    const itemIndex = documents.findIndex(
      (item: any) => item.MEDIA_ITEM_NUMBER === file.MEDIA_ITEM_NUMBER,
    );

    openResourceForm(
      {
        fields: DocumentFields,
        resourceType: ListingResource.Media,
        resourceIndex: itemIndex,
        internalResourceManagement: true,
      },
      {
        title: 'Document',
      },
      saveHook,
      file,
    );
  };

  const onDeletePhoto = () => {
    setIsConfirmCancel(false);
    deleteDocument(file.MED_VW_KEY);
  };

  const handlePermissionChange = (event: any) => {
    const newPermission = event.target.value as string;
    setPermissions(newPermission);

    // Create updated file record with new visibility
    const updatedFile: MediaResource = {
      ...file,
      MEDIA_VISIBILITY: newPermission,
    };

    const updatedDocuments = documents.map((doc) =>
      doc.MED_VW_KEY === file.MED_VW_KEY ? updatedFile : doc,
    );

    // Update media resources through the listing context
    updateMedia(updatedDocuments);
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.400',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'white',
        width: '380px',
        margin: '12px',
      }}
    >
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
        <CheckCircleIcon
          sx={{
            zIndex: '2',
            position: 'absolute',
            color: 'success.main',
            mr: 1,
            mt: 1,
            border: 1,
            borderRadius: 100,
            borderColor: 'white',
            backgroundColor: 'white',
            display: selectedFiles.includes(file.MED_VW_KEY) ? '' : 'none',
          }}
        />
      </Box>
      <Box
        onClick={onAddDocumentToSelected}
        doc-key={file.MED_VW_KEY ?? 'a'}
        sx={{ width: '380px', position: 'absolute', height: '180px' }}
      />
      <embed
        src={toHTTPSURL(file.MEDIA_URL)}
        type="application/pdf"
        width="100%"
        height="180px"
      />
      <Box sx={{ px: 3, my: 1 }}>
        <Typography sx={{ fontWeight: 'bold' }}>
          {file.MEDIA_LONG_DESCRIPTION}
        </Typography>
      </Box>
      <Box sx={{ px: 3, my: 2, display: 'flex' }}>
        <Typography sx={{ mr: 1, alignContent: 'center' }}>
          Permission type:
        </Typography>
        <Select
          size="small"
          value={permissions}
          data-testid={generateDataTestId(
            `${file.MEDIA_CATEGORY.toLowerCase()}-${file.MEDIA_DISPLAY_ORDER}`,
            undefined,
            'Permission type-selectField',
          )}
          onChange={handlePermissionChange}
        >
          <MenuItem
            key={MEDIA_VISIBILITY['Consumer Facing']}
            value="Consumer Facing"
          >
            {MEDIA_VISIBILITY['Consumer Facing']}
          </MenuItem>
          <MenuItem key={MEDIA_VISIBILITY.AgentOnly} value="AgentOnly">
            {MEDIA_VISIBILITY.AgentOnly}
          </MenuItem>
        </Select>
      </Box>
      <Divider className="bmls_divider" sx={{ mx: 3 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <MediaEditDeleteButtons
          props={{
            dataProps: file,
            isConfirmCancel,
            setIsConfirmCancel,
            editCallback: showEditSidePanel,
            deleteCallback: onDeletePhoto,
            isSmallDisplay,
          }}
        />
      </Box>
    </Box>
  );
};

export default DocumentTile;
